import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Button from '../common/Button';
import Table from '../common/Table';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import '../../styles/admin.css';

const SubscriptionPayments = () => {
  const [payments, setPayments] = useState([]);
  const [paybillTransactions, setPaybillTransactions] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [paymentPage, setPaymentPage] = useState(1);
  const [paybillPage, setPaybillPage] = useState(1);
  const [paymentPagination, setPaymentPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [paybillPagination, setPaybillPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paybillLoading, setPaybillLoading] = useState(true);
  const [verifyingPaymentId, setVerifyingPaymentId] = useState(null);
  const [resendingReceiptId, setResendingReceiptId] = useState(null);

  const loadPayments = useCallback(async (page = paymentPage, status = paymentStatus) => {
    setPaymentsLoading(true);
    try {
      const response = await api.get('/admin/subscription-payments', {
        params: { page, limit: 25, status }
      });
      setPayments(response.data.payments || []);
      setPaymentPagination({
        page: response.data.pagination?.page || page,
        pages: response.data.pagination?.pages || 1,
        total: response.data.pagination?.total || 0
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load subscription payments');
    } finally {
      setPaymentsLoading(false);
    }
  }, [paymentPage, paymentStatus]);

  const loadPaybillTransactions = useCallback(async () => {
    setPaybillLoading(true);
    try {
      const response = await api.get('/admin/paybill-transactions', {
        params: { page: paybillPage, limit: 25 }
      });
      setPaybillTransactions(response.data.transactions || []);
      setPaybillPagination({
        page: response.data.pagination?.page || paybillPage,
        pages: response.data.pagination?.pages || 1,
        total: response.data.pagination?.total || 0
      });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load Paybill transactions');
    } finally {
      setPaybillLoading(false);
    }
  }, [paybillPage]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  useEffect(() => {
    loadPaybillTransactions();
  }, [loadPaybillTransactions]);

  const handleVerifyReceipt = async (payment) => {
    if (!window.confirm(
      `Confirm that receipt ${payment.submitted_receipt} for ${formatCurrency(payment.amount)} was received in the Safaricom statement before activating this subscription.`
    )) {
      return;
    }

    setVerifyingPaymentId(payment.id);
    try {
      const response = await api.post(`/admin/subscription-payments/${payment.id}/verify`);
      toast.success(response.data.message || 'Payment verified');
      await loadPayments();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not verify payment');
    } finally {
      setVerifyingPaymentId(null);
    }
  };

  const handleResendReceipt = async (payment) => {
    if (!window.confirm(
      `Resend the PDF receipt for invoice ${payment.invoice_number || `#${payment.id}`} to the business and its active Business Admin email addresses?`
    )) {
      return;
    }

    setResendingReceiptId(payment.id);
    try {
      const response = await api.post(`/admin/subscription-payments/${payment.id}/resend-receipt`);
      toast.success(response.data.message || 'Receipt email resent');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not resend the receipt email');
    } finally {
      setResendingReceiptId(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1>Subscription Payments</h1>
          <p>Review all subscription transactions, check due payments, and verify customer-submitted receipts.</p>
        </div>
      </div>

      <Card title="Payment transactions">
        <p className="help-text">
          Receipt codes submitted by customers are claims, not proof of payment. Verify the code and amount
          against the Safaricom statement before activating any payment below.
        </p>
        <div className="payment-history-controls">
          <label htmlFor="subscription-payment-status">Show</label>
          <select
            id="subscription-payment-status"
            value={paymentStatus}
            onChange={(event) => {
              setPaymentStatus(event.target.value);
              setPaymentPage(1);
            }}
          >
            <option value="all">All transactions</option>
            <option value="pending">Due payments</option>
            <option value="completed">Paid</option>
            <option value="failed">Failed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <span>{paymentPagination.total} transaction{paymentPagination.total === 1 ? '' : 's'}</span>
        </div>
        <Table
          columns={[
            {
              key: 'invoice_number',
              label: 'Invoice',
              render: (value, row) => value || `#${row.id}`
            },
            { key: 'business_name', label: 'Business' },
            {
              key: 'plan_name',
              label: 'Plan',
              render: (value, row) => value
                ? `${value} (${row.billing_period || 'monthly'})`
                : 'Module subscription'
            },
            {
              key: 'amount',
              label: 'Amount',
              render: (value) => formatCurrency(value)
            },
            { key: 'account_reference', label: 'Account' },
            { key: 'payment_method', label: 'Method' },
            {
              key: 'status',
              label: 'Status',
              render: (value) => value === 'completed' ? 'Paid' : value === 'pending' ? 'Due' : value
            },
            { key: 'mpesa_receipt', label: 'Receipt' },
            { key: 'submitted_receipt', label: 'Submitted receipt' },
            {
              key: 'verify',
              label: 'Action',
              render: (_, row) => (
                <div className="payment-row-actions">
                  {row.status === 'pending' && row.submitted_receipt && (
                    <Button
                      variant="primary"
                      size="sm"
                      loading={verifyingPaymentId === row.id}
                      disabled={verifyingPaymentId !== null || resendingReceiptId !== null}
                      onClick={() => handleVerifyReceipt(row)}
                    >
                      Verify &amp; activate
                    </Button>
                  )}
                  {row.status === 'completed' && row.mpesa_receipt && (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={resendingReceiptId === row.id}
                      disabled={resendingReceiptId !== null || verifyingPaymentId !== null}
                      onClick={() => handleResendReceipt(row)}
                    >
                      Resend receipt
                    </Button>
                  )}
                </div>
              )
            },
            {
              key: 'created_at',
              label: 'Created',
              render: (value) => formatDate(value)
            }
          ]}
          data={payments}
          loading={paymentsLoading}
          pagination={paymentPagination}
          onPageChange={setPaymentPage}
          emptyMessage="No subscription payments found"
        />
      </Card>

      <Card title="Payments received by Paybill" className="mt-3">
        <p className="help-text">
          This list contains M-Pesa C2B confirmation transactions received after the callback was registered.
          Unmatched payments are still shown so you can compare their receipt, amount, and account reference.
        </p>
        <div className="payment-history-controls">
          <span>{paybillPagination.total} received transaction{paybillPagination.total === 1 ? '' : 's'}</span>
        </div>
        <Table
          columns={[
            { key: 'receipt_code', label: 'M-Pesa receipt' },
            {
              key: 'amount',
              label: 'Amount received',
              render: (value) => formatCurrency(value)
            },
            { key: 'bill_reference', label: 'Account / reference' },
            { key: 'phone_number', label: 'Phone' },
            {
              key: 'transaction_time',
              label: 'Transaction time',
              render: (value) => value || 'Not provided'
            },
            {
              key: 'invoice_number',
              label: 'Matched invoice',
              render: (value) => value || 'Unmatched'
            },
            {
              key: 'business_name',
              label: 'Business',
              render: (value) => value || '—'
            },
            {
              key: 'payment_status',
              label: 'Subscription status',
              render: (value) => value === 'completed' ? 'Activated' : value || 'Unmatched'
            }
          ]}
          data={paybillTransactions}
          loading={paybillLoading}
          pagination={paybillPagination}
          onPageChange={setPaybillPage}
          emptyMessage="No Paybill transactions received yet"
        />
      </Card>
    </div>
  );
};

export default SubscriptionPayments;
